<template>
  <div class="survey-page manage-links-page">
    <div class="survey-page-header">
      <div class="survey-page-header-left">
        <div class="survey-page-icon blue">
          <i class="bi bi-link-45deg"></i>
        </div>
        <div>
          <h5 class="survey-page-title">Manage Survey Links</h5>
          <p class="survey-page-sub">Multiple secure links per activity (Morning, Afternoon, custom sessions)</p>
        </div>
      </div>
    </div>

    <div class="survey-stat-row">
      <div class="survey-stat-card">
        <div class="stat-val">{{ stats.total }}</div>
        <div class="stat-lbl">Total links</div>
      </div>
      <div class="survey-stat-card">
        <div class="stat-val text-success">{{ stats.active }}</div>
        <div class="stat-lbl">Active</div>
      </div>
      <div class="survey-stat-card">
        <div class="stat-val text-danger">{{ stats.expired }}</div>
        <div class="stat-lbl">Expired</div>
      </div>
      <div class="survey-stat-card">
        <div class="stat-val">{{ stats.responses }}</div>
        <div class="stat-lbl">Total responses</div>
      </div>
    </div>

    <div class="toolbar-row">
      <div class="survey-search-wrap">
        <i class="bi bi-search"></i>
        <input
          v-model="searchQuery"
          type="search"
          class="form-control form-control-sm"
          placeholder="Search activities..."
        />
      </div>
      <div class="survey-filter-group">
        <button
          v-for="f in filters"
          :key="f.value"
          type="button"
          class="survey-filter-btn"
          :class="{ active: statusFilter === f.value }"
          @click="statusFilter = f.value"
        >
          {{ f.label }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="survey-loading">
      <div class="spinner-border spinner-border-sm text-primary mr-2"></div> Loading links...
    </div>

    <div v-else-if="!filteredSurveys.length" class="survey-empty">
      <div class="survey-empty-icon"><i class="bi bi-link-45deg"></i></div>
      <strong>No survey links found</strong>
      <p class="text-muted mb-0 mt-1">Generate a link from <strong>Manage Activity</strong> on any confirmed activity.</p>
    </div>

    <div v-else class="link-cards">
      <div v-for="s in filteredSurveys" :key="s.id" class="link-card" :class="{ 'is-expired': s.is_expired }">
        <div class="link-card-top">
          <div class="link-card-title">
            {{ s.activity_title || 'Untitled activity' }}
            <span v-if="s.instance_label || s.name" class="text-muted font-weight-normal"> · {{ s.instance_label || s.name }}</span>
          </div>
          <span class="survey-pill" :class="s.survey_type === 'online' ? 'online' : 'f2f'">
            <i :class="s.survey_type === 'online' ? 'bi bi-camera-video-fill' : 'bi bi-people-fill'"></i>
            {{ s.survey_type_label || (s.survey_type === 'online' ? 'Online' : 'Face-to-Face') }}
          </span>
        </div>

        <div v-if="s.activity_location" class="link-card-loc">
          <i class="bi bi-geo-alt"></i> {{ s.activity_location }}
        </div>

        <div class="link-card-meta">
          <div class="meta-item">
            <span class="meta-lbl">Generated</span>
            <span class="meta-val">{{ formatDate(s.generated_at) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-lbl">Expires</span>
            <span class="meta-val">{{ formatDate(s.expires_at) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-lbl">Responses</span>
            <span class="meta-val">{{ s.response_count }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-lbl">Status</span>
            <span class="survey-pill" :class="statusPillClass(s)">
              {{ statusLabel(s) }}
            </span>
          </div>
        </div>

        <div class="link-card-actions">
          <label
            class="action-btn open-toggle"
            :class="{ on: s.is_open && !s.is_expired, off: !s.is_open || s.is_expired }"
            :title="s.is_expired ? 'Expired surveys cannot be reopened' : (s.is_open ? 'Close survey' : 'Open survey')"
          >
            <input
              type="checkbox"
              :checked="!!s.is_open && !s.is_expired"
              :disabled="!!s.is_expired || !!s._toggling"
              @change="toggleOpen(s, $event)"
            />
            <i class="bi" :class="s.is_open && !s.is_expired ? 'bi-unlock-fill' : 'bi-lock-fill'"></i>
            {{ s.is_open && !s.is_expired ? 'Open' : 'Closed' }}
          </label>
          <button class="action-btn" :disabled="!s.survey_url" @click="openQr(s)">
            <i class="bi bi-qr-code"></i> QR Code
          </button>
          <button class="action-btn" :disabled="!s.survey_url" @click="copyLink(s)">
            <i class="bi bi-clipboard"></i> Copy
          </button>
          <button class="action-btn action-btn-accent" @click="viewResponses(s)">
            <i class="bi bi-bar-chart-line"></i> Responses
          </button>
        </div>
      </div>
    </div>

    <SurveyLinkQrModal ref="qrModal" />
  </div>
</template>

<script>
import "@/css/surveys.css";
import axios from "axios";
import moment from "moment";
import SurveyLinkQrModal from "./components/SurveyLinkQrModal.vue";

export default {
  name: "ManageSurveyLinks",
  components: { SurveyLinkQrModal },
  data() {
    return {
      loading: true,
      surveys: [],
      searchQuery: "",
      statusFilter: "all",
      filters: [
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "expired", label: "Expired" },
      ],
    };
  },
  computed: {
    filteredSurveys() {
      let list = this.surveys;
      if (this.statusFilter === "active") list = list.filter((s) => !s.is_expired);
      else if (this.statusFilter === "expired") list = list.filter((s) => s.is_expired);
      const q = this.searchQuery.trim().toLowerCase();
      if (q) list = list.filter((s) => (s.activity_title || "").toLowerCase().includes(q));
      return list;
    },
    stats() {
      return {
        total: this.surveys.length,
        active: this.surveys.filter((s) => !s.is_expired).length,
        expired: this.surveys.filter((s) => s.is_expired).length,
        responses: this.surveys.reduce((sum, s) => sum + (s.response_count || 0), 0),
      };
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get("api/surveys", { params: { status: this.statusFilter } });
        this.surveys = data.surveys || [];
      } catch (e) {
        toastr.error("Failed to load survey links.", "Error");
      } finally {
        this.loading = false;
      }
    },
    formatDate(dt) {
      return dt ? moment(dt).format("MMM D, YYYY h:mm A") : "—";
    },
    statusLabel(s) {
      if (s.is_expired) return "Expired";
      return s.is_open === false ? "Closed" : "Open";
    },
    statusPillClass(s) {
      if (s.is_expired) return "expired";
      return s.is_open === false ? "expired" : "active";
    },
    async toggleOpen(survey, event) {
      if (survey.is_expired) {
        event.target.checked = false;
        return;
      }
      const next = !!event.target.checked;
      this.$set(survey, "_toggling", true);
      try {
        const { data } = await axios.patch(`api/surveys/${survey.id}/open`, { is_open: next });
        survey.is_open = data.is_open;
        survey.is_active = data.is_active;
        toastr.success(data.message || (next ? "Survey opened." : "Survey closed."), "Survey");
      } catch (e) {
        event.target.checked = !next;
        toastr.error(e.response?.data?.message || "Failed to update survey.", "Error");
      } finally {
        this.$set(survey, "_toggling", false);
      }
    },
    openQr(survey) {
      if (!survey.survey_url) {
        toastr.warning("This link has expired.", "Expired");
        return;
      }
      this.$refs.qrModal.open(survey);
    },
    copyLink(survey) {
      if (!survey.survey_url) {
        toastr.warning("This link has expired.", "Expired");
        return;
      }
      const el = document.createElement("textarea");
      el.value = survey.survey_url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      toastr.success("Survey link copied.", "Copied");
    },
    viewResponses(survey) {
      this.$router.push({
        name: "SurveyResponses",
        query: {
          event_id: survey.calendar_event_id,
          survey_id: survey.id,
        },
      });
    },
  },
  watch: {
    statusFilter() { this.load(); },
  },
};
</script>

<style scoped>
.manage-links-page { width: 100%; }

.toolbar-row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 1.25rem;
}

.link-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 0.85rem;
}

.link-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 21, 0.08);
  border-radius: 14px;
  padding: 1.1rem;
  box-shadow: 0 4px 20px rgba(43, 58, 85, 0.05);
  transition: box-shadow 0.15s ease;
}

.link-card:hover { box-shadow: 0 6px 28px rgba(43, 58, 85, 0.09); }
.link-card.is-expired { opacity: 0.82; }

.link-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.link-card-title {
  font-weight: 700;
  font-size: 0.92rem;
  color: #2b3a55;
  line-height: 1.35;
}

.link-card-loc {
  font-size: 0.78rem;
  color: #9aa5b4;
  margin-bottom: 0.75rem;
}

.link-card-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  padding: 0.65rem;
  background: #f8f9fb;
  border-radius: 10px;
}

.meta-lbl {
  display: block;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9aa5b4;
}

.meta-val {
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
}

.link-card-actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  min-width: 0;
  border: 1px solid rgba(0, 0, 21, 0.1);
  background: #fff;
  border-radius: 9px;
  padding: 0.4rem 0.5rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover:not(:disabled) {
  border-color: rgba(77, 141, 255, 0.35);
  color: #4d8dff;
  background: rgba(77, 141, 255, 0.04);
}

.action-btn-accent {
  background: rgba(77, 141, 255, 0.08);
  border-color: rgba(77, 141, 255, 0.2);
  color: #4d8dff;
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.open-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin: 0;
}

.open-toggle input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.open-toggle.on {
  border-color: rgba(43, 182, 163, 0.35);
  background: rgba(43, 182, 163, 0.1);
  color: #1f8f7f;
}

.open-toggle.off {
  border-color: rgba(229, 83, 83, 0.3);
  background: rgba(229, 83, 83, 0.08);
  color: #e55353;
}
</style>
